import { styled } from '@mui/system';
import { Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { LeaderboardAggregation, LeaderboardUser } from 'types/Leaderboard';
import { Box, keyframes } from '@mui/system';
import { useDispatch } from 'react-redux';
import { showHideInfoDialog } from 'state/tasks/tasksSlice';
import Loading from 'components/Loading';
import { CANDYCANE_RED, HEADER_SIZE_PX } from 'styles/AOCTheme';
import { useParams } from 'react-router-dom';
import { getLeaderboard, getLeaderboardWithSharedToken } from 'API/leaderboard';
import { ParamTypes } from 'types/ParamTypes';
import { getLeaderboardTitle } from 'utils/getLeaderboardTitle';
import { getTypography } from 'components/TaskLeaderboard/Index';
import { getCurrentSeasonInfo } from '../Background/season';

const leaderboardAnimation = keyframes`
	from { transform: translateY(0%); }
	to { transform: translateY(-100%); }`;

const CONTAINER_PADDING = 16;

const prefix = 'Leaderboard';
const classes = {
	title: `${prefix}-title`,
	leaderboardHeader: `${prefix}-leaderboardHeader`,
	leaderboardContainer: `${prefix}-leaderboardContainer`,
	leaderboardOptionsPrimary: `${prefix}-leaderboardOptionsPrimary`,
	leaderboardOptions: `${prefix}-leaderboardOptions`,
};
const StyledDiv = styled('div')(() => ({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: 'max-content',
	flexDirection: 'column',
	overflow: 'hidden',
}));

const StyledBox = styled(Box)(({ theme }) => ({
	[`&.${classes.leaderboardHeader}`]: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 1fr)',
		gridRowGap: '16px',
		width: '100%',
		placeItems: 'center',
		zIndex: 100,
		'& > *': {
			fontFamily: '\'Press Start 2P\', \'cursive\'',
			[theme.breakpoints.down('sm')]: {
				fontSize: '1rem !important',
			},
		},
	},
	[`&.${classes.leaderboardContainer}`]: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 1fr)',
		gridRowGap: '16px',
		width: '100%',
		placeItems: 'center',
		padding: `${CONTAINER_PADDING}px 0`,
		'& > *': {
			fontFamily: '\'Press Start 2P\', \'cursive\'',
		},
	},
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
	[`&.${classes.title}`]: {
		margin: '3rem 0 2rem 0 !important',
		textAlign: 'center',
		textTransform: 'uppercase',
		[theme.breakpoints.down('sm')]: {
			fontSize: '1.5rem !important',
		},
	},
	[`&.${classes.leaderboardOptionsPrimary}`]: {
		color: 'gold',
		cursor: 'pointer',
		'&:hover': {
			textShadow: '0 0 5px gold',
		},
	},
	[`&.${classes.leaderboardOptions}`]: {
		color: CANDYCANE_RED,
		cursor: 'pointer',
		'&:hover': {
			textShadow: `0 0 5px ${CANDYCANE_RED}`,
		},
	},
}));

interface ILeaderboard {
	isTv?: boolean;
}
const Leaderboard: React.FC<ILeaderboard> = (props) => {
	const [eventName, setEventName] = useState<string>('');
	const { isTv = false } = props;
	const dispatch = useDispatch();
	const [users, setUsers] = useState<LeaderboardUser[]>([]);
	const [leaderboardAggregation, setLeaderboardAggregation] = useState<LeaderboardAggregation>({});
	const [selectedTab, setSelectedTab] = useState<string>('general');
	const [baseLeaderboardData, setBaseLeaderboardData] = useState<any>({});
	const [isLoading, setLoading] = useState(true);
	const [canScroll, setCanScroll] = useState(false);
	const { eventId } = useParams() as ParamTypes;
	const usersContainerRef = useRef<HTMLElement>();
	const DURATION = 50;
	const INTERVAL_DURATION = 300000;

	const COLORS = getCurrentSeasonInfo().letterColorSequence;

	useEffect(() => {
		const fetchLeaderboard = async () => {
			const params = new URLSearchParams(window.location.search);
			const sharedToken = params.get('token');
			let data;

			try {
				if (sharedToken) {
					data = await getLeaderboardWithSharedToken(eventId, sharedToken);
				} else {
					data = await getLeaderboard(eventId);
				}
				setBaseLeaderboardData(data);
				setEventName(data.eventName);
				setUsers(data.leaderboardData);
				setLeaderboardAggregation(data.achievementsAggregation);
			} catch (e) {
				if (e instanceof Error) {
					if (!isTv) {
						dispatch(showHideInfoDialog({ title: 'Napaka', message: e.message }));
					} else {
						// eslint-disable-next-line no-console
						console.log(e);
					}
				}
			} finally {
				setLoading(false);
			}
		};

		fetchLeaderboard();
		const interval = setInterval(fetchLeaderboard, INTERVAL_DURATION);

		return () => clearInterval(interval);
	}, [dispatch, eventId, isTv]);

	useEffect(() => {
		if (!isTv) {
			return;
		}
		const interval = setInterval(() => {
			const usersContainerBoundingRectBottom = usersContainerRef.current?.getBoundingClientRect().bottom;
			const contentBottom = (usersContainerBoundingRectBottom ?? 0) + HEADER_SIZE_PX + (CONTAINER_PADDING * 2);
			const canScrollValue = contentBottom > document.body.clientHeight;

			if (canScrollValue) {
				setCanScroll(canScrollValue);
				clearInterval(interval);

				return;
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [isTv]);

	const isGeneralTabSelected = selectedTab === 'general';
	const aggregationLeaderboardEntries = Object.entries(leaderboardAggregation);
	const boldify = (data: string, bold: boolean) => bold ? <b>{data}</b> : data;

	const numOfHelpers: number = baseLeaderboardData.eventParticipants ?? 0;
	const rankingName: null | string = isGeneralTabSelected
		? 'Točke'
		: baseLeaderboardData.userRanking?.globalAchievement?.[selectedTab]?.name ?? null;
	const userRanking: null | number = isGeneralTabSelected
		? baseLeaderboardData.userRanking?.global?.rank ?? null
		: baseLeaderboardData.userRanking?.globalAchievement?.[selectedTab]?.rank ?? null;
	const userRankingValue: null | string = isGeneralTabSelected
		? baseLeaderboardData.userRanking?.global?.points ?? null
		: baseLeaderboardData.userRanking?.globalAchievement?.[selectedTab]?.value ?? null;
	const leaderboardUpdatedAt: null | string = baseLeaderboardData?.calculatedAt ?? null;

	const showRanking = userRanking && userRankingValue;
	const buttonAnchorStyle = { textDecoration: 'none', color: 'unset', textShadow: 'inherit' };

	return (<>
		{!isLoading && leaderboardUpdatedAt && <p style={{ fontFamily: '\'Press Start 2P\'', fontSize: '0.5rem', textAlign: 'center', marginBottom: 5 }}>Zadnja avtomatska posodobitev: {new Date(leaderboardUpdatedAt).toLocaleString('sl-SI', { timeZone: 'UTC' })} (UTC)</p>}
		{!isLoading && !isTv && <Box display={'flex'} justifyContent={'center'}>
			{<StyledTypography onClick={() => setSelectedTab('general')} className={classes.leaderboardOptionsPrimary} style={{ color: 'gold', textShadow: isGeneralTabSelected ? '0 0 10px gold' : '' }}><a style={buttonAnchorStyle}>{boldify('[🏆Generalna lestvica🏆]', isGeneralTabSelected)}</a></StyledTypography>}
			{aggregationLeaderboardEntries.map(([key, values]) => {
				const title = getLeaderboardTitle(key) ?? '???';
				const isSelected = selectedTab === key;
				const enabled = values?.length > 0;
				const isOptimum = key === 'bestOptimizer';
				const disabledText = isOptimum ? 'Lestvica trenutno ni na voljo, saj se še ni našel Božičkov pomočnik, ki bi rešil optimizacijsko nalogo.' : 'Lestvica trenutno ni na voljo, saj še noben Božičkov pomagač ni rešil vseh odprtih nalog (glej pravila). Če želiš, lahko vidiš izbrano lokalno lestvico pri seznamu nalog.';

				return <Tooltip key={key} title={enabled ? '' : disabledText} >
					<StyledTypography sx={{ ...(enabled ? {} : { color: 'rgba(0, 0, 0, 0.38)!important' }) }} key={key} style={{ textShadow: selectedTab === key ? `0 0 10px ${CANDYCANE_RED}` : '' }} onClick={() => enabled ? setSelectedTab(key) : null} className={classes.leaderboardOptions}><a style={buttonAnchorStyle}>{boldify(`[${title}]`, isSelected)}</a></StyledTypography>
				</Tooltip>;

			})}
		</Box>}
		{eventName && <StyledTypography variant='h2' className={classes.title}>{eventName} - Lestvica</StyledTypography>}
		{!isLoading && showRanking && <Typography variant='h4' style={{ fontSize: '0.9rem', textAlign: 'center', marginBottom: '3rem' }}>zavzemaš <span style={{ color: 'gold', fontWeight: 'bold' }}>{userRanking}.</span> mesto na lestvici ({rankingName}: <span style={{ color: 'gold', fontWeight: 'bold' }}>{userRankingValue}</span>)</Typography>}
		{!isLoading && <Typography variant='h4' style={{ fontSize: '0.9rem', textAlign: 'center', marginBottom: '3rem' }}>Število Božičkovih pomočnikov: {numOfHelpers}</Typography>}
		<StyledBox component='div' className={classes.leaderboardHeader} >
			{isLoading && <div style={{
				position: 'absolute',
				left: '50%',
				top: '50%',
				transform: 'translate(-50%, -50%)',
			}}><Loading /></div>}
			{!isLoading && <>
				<Typography variant='h4' style={{ color: 'rgba(255,221,0, 0.8)' }}>mesto</Typography>
				<Typography variant='h4' style={{ color: 'rgba(255,221,0, 0.8)' }}>{isGeneralTabSelected ? 'točke' : leaderboardAggregation[selectedTab]?.[0]?.columnName ?? 'točke'}</Typography>
				<Typography variant='h4' style={{ color: 'rgba(255,221,0, 0.8)' }}>ime</Typography>
			</>}
		</StyledBox>
		<StyledDiv>
			<StyledBox className={classes.leaderboardContainer} component='div' ref={usersContainerRef} id='usersContainer' sx={{ animation: canScroll ? `${leaderboardAnimation} ${DURATION}s linear infinite` : '', transitionDelay: `${DURATION / 2}s` }}>
				{isGeneralTabSelected ? users.map((user, index) => {
					const color = COLORS[index % COLORS.length];

					return (<React.Fragment key={index}>
						{user.isBold ? <em>{getTypography(user.rank, color)}</em> : getTypography(user.rank, color)}
						{user.isBold ? <em>{getTypography(user.score, color)}</em> : getTypography(user.score, color)}
						{(user.isBold ? <em>{getTypography(user.displayName, color, true)}</em> : getTypography(user.displayName, color, true))}
					</React.Fragment>);
				}) : leaderboardAggregation[selectedTab]?.map((item, index) => {
					const color = COLORS[index % COLORS.length];

					return (<React.Fragment key={index}>
						{item.isBold ? <em>{getTypography(item.rank, color)}</em> : getTypography(item.rank, color)}
						{item.isBold ? <em>{getTypography(item.columnValue, color)}</em> : getTypography(item.columnValue, color)}
						{item.isBold ? <em>{getTypography(item.displayName, color, true)}</em> : getTypography(item.displayName, color, true)}
					</React.Fragment>);
				})}
			</StyledBox>
			{canScroll && <StyledBox className={classes.leaderboardContainer} component='div' sx={{ animation: `${leaderboardAnimation} ${DURATION}s linear infinite`, transitionDelay: `${DURATION / 2}s` }}>
				{users.map((user, index) => {
					const color = COLORS[index % COLORS.length];

					return (<React.Fragment key={index}>
						{user.isBold ? <em>{getTypography(user.rank, color)}</em> : getTypography(user.rank, color)}
						{user.isBold ? <em>{getTypography(user.score, color)}</em> : getTypography(user.score, color)}
						{(user.isBold ? <em>{getTypography(user.displayName, color, true)}</em> : getTypography(user.displayName, color, true))}
					</React.Fragment>);
				})}
			</StyledBox>}
		</StyledDiv>
	</>
	);
};

export default Leaderboard;
